import useDocument from 'CustomHooks/useDocument';
import useLoadedFor from 'CustomHooks/useLoadedFor';
import { SecurityPlus, AppSettings } from 'types/shared/documents/restaurants';
import { ExperimentalFlags } from 'App/ReservationBook/Components/ExperimentalFlags';

type Read = { data: unknown; error: string | null };

// A failed read ends `loading` as well, but it is no missing document.
const unread = (doc: Read) => !!doc.error && !doc.data;

/**
 * What Security Plus protects in a restaurant: its settings and the
 * experimentalFlags that may exempt admins (disableSecurityForAdmins).
 * `loaded` only once both were read for this restaurant; until then, also
 * after a failed read, the policy is unknown (usePinAuth waits).
 */
const useSecurityPlusPolicy = (restaurantId: string | null) => {
  const [securityPlusDoc] = useDocument<AppSettings<SecurityPlus>>(
    `restaurants/${restaurantId}/apps`,
    'security_plus',
  );

  const [experimentalFlags] = useDocument<ExperimentalFlags>(
    `restaurants/${restaurantId}/other`,
    'experimentalFlags',
  );

  const loaded = useLoadedFor(
    `${restaurantId}`,
    securityPlusDoc.loading ||
      experimentalFlags.loading ||
      unread(securityPlusDoc) ||
      unread(experimentalFlags),
  );

  return { securityPlusDoc, experimentalFlags, loaded };
};

export default useSecurityPlusPolicy;
